import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DashboardOutlined as DashboardOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  ViewListOutlined as ViewListOutlinedIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader/PageHeader';
import StaffTaskService from '../../services/StaffTaskService';
import { getProfile, getStaff } from '../../services/UserService';
import { getLocations } from '../../services/LocationService';
import { permissionOptions } from '../../utils/Enums';
import SnackbarUtils from '../../utils/SnackbarUtils';

const TASK_TYPE_OPTIONS = [
  { value: 'ONE_TIME', label: 'Puntual' },
  { value: 'RECURRING', label: 'Recurrente' },
];

const RECURRENCE_TYPE_OPTIONS = [
  { value: 'WEEKLY', label: 'Semanal (días de semana)' },
  { value: 'MONTHLY', label: 'Mensual (uno o más días del mes)' },
];

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendientes' },
  { value: 'COMPLETED', label: 'Completadas' },
  { value: 'ALL', label: 'Todas' },
];

const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' },
];

const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => ({
  value: index + 1,
  label: `${index + 1}`,
}));

const TASK_VIEW_MODE = {
  LIST: 'LIST',
  BOARD: 'BOARD',
};

const BOARD_DAY_OFFSETS = [-1, 0, 1];

const getPermissionId = (label) => permissionOptions.find((permission) => permission.label === label)?.id;

const emptyForm = {
  title: '',
  description: '',
  assigneeEmail: '',
  locationIds: [],
  taskType: 'ONE_TIME',
  startsOn: dayjs(),
  dueTime: null,
  recurrenceType: 'WEEKLY',
  weeklyDays: [],
  monthlyDays: [],
};

const formatPersonName = (staffMember) => {
  if (!staffMember) return '';
  const fullName = `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim();
  return fullName || staffMember.email;
};

const formatOccurrenceLabel = (occurrence) => {
  const dateLabel = occurrence.occurrenceDate
    ? dayjs(occurrence.occurrenceDate).format('DD/MM/YYYY')
    : '-';
  const timeLabel = occurrence.dueTime ? occurrence.dueTime.slice(0, 5) : 'Sin hora';
  return `${dateLabel} · ${timeLabel}`;
};

const formatBoardColumnTitle = (date) => {
  if (date.isSame(dayjs(), 'day')) {
    return `Hoy · ${date.format('DD/MM')}`;
  }

  return `${date.format('ddd')} · ${date.format('DD/MM')}`;
};

const buildPayload = (formData) => ({
  title: formData.title.trim(),
  description: formData.description.trim() || null,
  assignee_email: formData.assigneeEmail,
  location_ids: formData.locationIds,
  task_type: formData.taskType,
  starts_on: formData.startsOn?.format('YYYY-MM-DD'),
  due_time: formData.dueTime ? formData.dueTime.format('HH:mm:ss') : null,
  recurrence_type: formData.taskType === 'RECURRING' ? formData.recurrenceType : null,
  weekly_days: formData.taskType === 'RECURRING' && formData.recurrenceType === 'WEEKLY' ? formData.weeklyDays : [],
  monthly_days: formData.taskType === 'RECURRING' && formData.recurrenceType === 'MONTHLY' ? formData.monthlyDays : [],
});

const TaskFormDialog = ({
  open,
  onClose,
  onSave,
  saving,
  staffOptions,
  locationOptions,
  initialValue,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  const steps = ['Qué se va a hacer', 'En dónde', 'Cuándo'];

  useEffect(() => {
    if (!open) return;

    if (initialValue) {
      setFormData({
        title: initialValue.title || '',
        description: initialValue.description || '',
        assigneeEmail: initialValue.assigneeEmail || '',
        locationIds: initialValue.locationIds || [],
        taskType: initialValue.taskType || 'ONE_TIME',
        startsOn: initialValue.startsOn ? dayjs(initialValue.startsOn) : dayjs(),
        dueTime: initialValue.dueTime ? dayjs(`2025-01-01T${initialValue.dueTime}`) : null,
        recurrenceType: initialValue.recurrenceType || 'WEEKLY',
        weeklyDays: Array.isArray(initialValue.weeklyDays) ? initialValue.weeklyDays : [],
        monthlyDays: Array.isArray(initialValue.monthlyDays)
          ? initialValue.monthlyDays
          : (initialValue.monthlyDayOfMonth ? [initialValue.monthlyDayOfMonth] : []),
      });
    } else {
      setFormData({
        ...emptyForm,
        startsOn: dayjs(),
      });
    }
    setErrors({});
    setActiveStep(0);
  }, [initialValue, open]);

  const selectedStaff = useMemo(
    () => staffOptions.find((staffMember) => staffMember.email === formData.assigneeEmail) || null,
    [formData.assigneeEmail, staffOptions]
  );
  const safeWeeklyDays = Array.isArray(formData.weeklyDays) ? formData.weeklyDays : [];
  const safeMonthlyDays = Array.isArray(formData.monthlyDays)
    ? formData.monthlyDays
    : (initialValue?.monthlyDayOfMonth ? [initialValue.monthlyDayOfMonth] : []);

  const assigneeLocations = useMemo(() => {
    if (!selectedStaff) return [];
    const locationIdSet = new Set(selectedStaff.locationIds || []);
    return locationOptions.filter((location) => locationIdSet.has(location.id));
  }, [locationOptions, selectedStaff]);

  useEffect(() => {
    if (!selectedStaff) return;

    if (assigneeLocations.length === 1) {
      const onlyLocationId = assigneeLocations[0].id;
      setFormData((previous) => ({
        ...previous,
        locationIds: [onlyLocationId],
      }));
      return;
    }

    setFormData((previous) => {
      const filteredLocationIds = previous.locationIds.filter((locationId) =>
        assigneeLocations.some((location) => location.id === locationId)
      );
      return filteredLocationIds.length === previous.locationIds.length
        ? previous
        : { ...previous, locationIds: filteredLocationIds };
    });
  }, [assigneeLocations, selectedStaff]);

  const handleStaffChange = (_, value) => {
    setFormData((previous) => ({
      ...previous,
      assigneeEmail: value?.email || '',
      locationIds: [],
    }));
  };

  const handleLocationChange = (_, values) => {
    setFormData((previous) => ({
      ...previous,
      locationIds: values.map((location) => location.id),
    }));
  };

  const handleWeekdayToggle = (weekdayValue) => {
    setFormData((previous) => {
      const previousWeeklyDays = Array.isArray(previous.weeklyDays) ? previous.weeklyDays : [];
      const exists = previousWeeklyDays.includes(weekdayValue);
      const nextValues = exists
        ? previousWeeklyDays.filter((value) => value !== weekdayValue)
        : [...previousWeeklyDays, weekdayValue];

      return {
        ...previous,
        weeklyDays: nextValues.sort((a, b) => a - b),
      };
    });
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.title.trim()) {
      nextErrors.title = t('required_field') || 'Campo requerido';
    }
    if (!formData.assigneeEmail) {
      nextErrors.assigneeEmail = t('select_user') || 'Selecciona un responsable';
    }
    if (!formData.locationIds.length) {
      nextErrors.locationIds = t('select_branch') || 'Selecciona al menos una sucursal';
    }
    if (!formData.startsOn || !formData.startsOn.isValid()) {
      nextErrors.startsOn = t('required_field') || 'Campo requerido';
    }
    if (formData.taskType === 'RECURRING' && formData.recurrenceType === 'WEEKLY' && !safeWeeklyDays.length) {
      nextErrors.weeklyDays = 'Selecciona al menos un día';
    }
    if (formData.taskType === 'RECURRING' && formData.recurrenceType === 'MONTHLY') {
      if (!safeMonthlyDays.length) {
        nextErrors.monthlyDays = 'Selecciona al menos un día del mes';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep = (step) => {
    const nextErrors = {};

    if (step === 0) {
      if (!formData.title.trim()) {
        nextErrors.title = t('required_field') || 'Campo requerido';
      }
    }

    if (step === 1) {
      if (!formData.assigneeEmail) {
        nextErrors.assigneeEmail = t('select_user') || 'Selecciona un responsable';
      }
      if (!formData.locationIds.length) {
        nextErrors.locationIds = t('select_branch') || 'Selecciona al menos una sucursal';
      }
    }

    if (step === 2) {
      if (!formData.startsOn || !formData.startsOn.isValid()) {
        nextErrors.startsOn = t('required_field') || 'Campo requerido';
      }
      if (formData.taskType === 'RECURRING' && formData.recurrenceType === 'WEEKLY' && !safeWeeklyDays.length) {
        nextErrors.weeklyDays = 'Selecciona al menos un día';
      }
      if (formData.taskType === 'RECURRING' && formData.recurrenceType === 'MONTHLY') {
        if (!safeMonthlyDays.length) {
          nextErrors.monthlyDays = 'Selecciona al menos un día del mes';
        }
      }
    }

    setErrors((previous) => ({ ...previous, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const handleNextStep = () => {
    if (!validateStep(activeStep)) {
      SnackbarUtils.error(t('fill_required_fields') || 'Completa los campos requeridos');
      return;
    }
    setActiveStep((previous) => Math.min(previous + 1, steps.length - 1));
  };

  const handlePreviousStep = () => {
    setActiveStep((previous) => Math.max(previous - 1, 0));
  };

  const handleSubmit = () => {
    if (!validate()) {
      SnackbarUtils.error(t('fill_required_fields') || 'Completa los campos requeridos');
      return;
    }
    onSave(buildPayload(formData));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{initialValue ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {activeStep === 0 && (
            <>
              <Grid item xs={12}>
                <TextField
                  label="Título"
                  value={formData.title}
                  onChange={(event) => setFormData((previous) => ({ ...previous, title: event.target.value }))}
                  fullWidth
                  error={Boolean(errors.title)}
                  helperText={errors.title}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descripción"
                  value={formData.description}
                  onChange={(event) => setFormData((previous) => ({ ...previous, description: event.target.value }))}
                  fullWidth
                  multiline
                  minRows={4}
                />
              </Grid>
            </>
          )}

          {activeStep === 1 && (
            <>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={selectedStaff}
                  options={staffOptions}
                  onChange={handleStaffChange}
                  getOptionLabel={formatPersonName}
                  isOptionEqualToValue={(option, value) => option.email === value.email}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Responsable"
                      error={Boolean(errors.assigneeEmail)}
                      helperText={errors.assigneeEmail}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={assigneeLocations}
                  value={assigneeLocations.filter((location) => formData.locationIds.includes(location.id))}
                  onChange={handleLocationChange}
                  disabled={!selectedStaff || assigneeLocations.length === 1}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} sx={{ mr: 1 }} />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursales"
                      error={Boolean(errors.locationIds)}
                      helperText={
                        errors.locationIds ||
                        (assigneeLocations.length === 1 ? 'Se asigna automáticamente la única sucursal del responsable' : '')
                      }
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {activeStep === 2 && (
            <>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={formData.taskType}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        taskType: event.target.value,
                        recurrenceType: event.target.value === 'RECURRING' ? previous.recurrenceType : 'WEEKLY',
                        weeklyDays: event.target.value === 'RECURRING'
                          ? (Array.isArray(previous.weeklyDays) ? previous.weeklyDays : [])
                          : [],
                        monthlyDays: event.target.value === 'RECURRING'
                          ? (Array.isArray(previous.monthlyDays) ? previous.monthlyDays : [])
                          : [],
                      }))
                    }
                  >
                    {TASK_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label={formData.taskType === 'ONE_TIME' ? 'Fecha' : 'Desde'}
                  format="DD/MM/YYYY"
                  value={formData.startsOn}
                  onChange={(value) => setFormData((previous) => ({ ...previous, startsOn: value }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.startsOn),
                      helperText:
                        errors.startsOn ||
                        (formData.taskType === 'ONE_TIME'
                          ? 'Fecha en la que se hace la tarea'
                          : 'Fecha desde la que empieza la recurrencia'),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TimePicker
                  label="Hora"
                  value={formData.dueTime}
                  onChange={(value) => setFormData((previous) => ({ ...previous, dueTime: value }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Opcional',
                    },
                  }}
                />
              </Grid>

              {formData.taskType === 'RECURRING' && (
                <>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Recurrencia</InputLabel>
                      <Select
                        label="Recurrencia"
                        value={formData.recurrenceType}
                        onChange={(event) =>
                          setFormData((previous) => ({
                            ...previous,
                            recurrenceType: event.target.value,
                            weeklyDays: event.target.value === 'WEEKLY'
                              ? (Array.isArray(previous.weeklyDays) ? previous.weeklyDays : [])
                              : [],
                            monthlyDays: event.target.value === 'MONTHLY'
                              ? (Array.isArray(previous.monthlyDays) ? previous.monthlyDays : [])
                              : [],
                          }))
                        }
                      >
                        {RECURRENCE_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {formData.recurrenceType === 'WEEKLY'
                        ? 'Semanal: elegís uno o más días de la semana y la tarea se repite en esos días.'
                        : 'Mensual: elegís uno o más días del mes. Ejemplo: 5 y 20 = todos los meses el día 5 y 20. Si un mes no tiene alguno de esos días, se usa el último día del mes.'}
                    </Alert>
                  </Grid>

                  {formData.recurrenceType === 'MONTHLY' && (
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={MONTH_DAY_OPTIONS}
                        value={MONTH_DAY_OPTIONS.filter((option) => safeMonthlyDays.includes(option.value))}
                        onChange={(_, values) =>
                          setFormData((previous) => ({
                            ...previous,
                            monthlyDays: values.map((value) => value.value).sort((a, b) => a - b),
                          }))
                        }
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox checked={selected} sx={{ mr: 1 }} />
                            Día {option.label}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Días del mes"
                            error={Boolean(errors.monthlyDays)}
                            helperText={errors.monthlyDays || 'Podés elegir varios, por ejemplo 5 y 20'}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  {formData.recurrenceType === 'WEEKLY' && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Días de la semana
                      </Typography>
                      <FormGroup row>
                        {WEEKDAY_OPTIONS.map((weekday) => (
                          <FormControlLabel
                            key={weekday.value}
                            control={
                              <Checkbox
                                checked={safeWeeklyDays.includes(weekday.value)}
                                onChange={() => handleWeekdayToggle(weekday.value)}
                              />
                            }
                            label={weekday.label}
                          />
                        ))}
                      </FormGroup>
                      {errors.weeklyDays && (
                        <Typography variant="caption" color="error">
                          {errors.weeklyDays}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('cancel') || 'Cancelar'}
        </Button>
        {activeStep > 0 && (
          <Button onClick={handlePreviousStep} disabled={saving}>
            Atrás
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button onClick={handleNextStep} variant="contained" disabled={saving}>
            Siguiente
          </Button>
        ) : (
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : initialValue ? 'Guardar cambios' : 'Crear tarea'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const TaskDeleteDialog = ({
  open,
  onClose,
  onDeleteOccurrence,
  onDeleteTask,
  deleting,
  occurrence,
}) => {
  if (!occurrence) return null;

  const isRecurring = occurrence.taskType === 'RECURRING';

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Eliminar tarea</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            {isRecurring
              ? 'Esta tarea es recurrente. Elegí si querés borrar solamente esta ocurrencia o cancelar todas las pendientes de esta tarea.'
              : 'Esta es una tarea puntual. Si la eliminás, se borra esta tarea.'}
          </Typography>
          <Alert severity="warning">
            {isRecurring
              ? `Ocurrencia seleccionada: ${formatOccurrenceLabel(occurrence)}`
              : `Tarea seleccionada: ${formatOccurrenceLabel(occurrence)}`}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancelar
        </Button>
        {isRecurring && (
          <Button onClick={onDeleteOccurrence} color="warning" disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Solo esta tarea'}
          </Button>
        )}
        <Button onClick={isRecurring ? onDeleteTask : onDeleteOccurrence} color="error" variant="contained" disabled={deleting}>
          {deleting ? <CircularProgress size={18} color="inherit" /> : (isRecurring ? 'Todas las pendientes' : 'Eliminar tarea')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TaskCompleteDialog = ({
  open,
  onClose,
  onConfirm,
  completing,
  occurrence,
}) => {
  if (!occurrence) return null;

  return (
    <Dialog open={open} onClose={completing ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Confirmar tarea</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            ¿Querés marcar esta tarea como completada?
          </Typography>
          <Alert severity="info">
            {`${occurrence.title} · ${formatOccurrenceLabel(occurrence)}`}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={completing}>
          Cancelar
        </Button>
        <Button onClick={onConfirm} variant="contained" color="success" disabled={completing}>
          {completing ? <CircularProgress size={18} color="inherit" /> : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TaskOccurrenceActions = ({
  occurrence,
  isBusy,
  actionLoadingId,
  onComplete,
  onReopen,
  onEdit,
  onDelete,
}) => (
  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
    {occurrence.canComplete && (
      <IconButton
        color="success"
        onClick={() => onComplete(occurrence)}
        disabled={isBusy}
        title="Completar"
      >
        {isBusy ? <CircularProgress size={18} /> : <CheckCircleOutlineIcon />}
      </IconButton>
    )}
    {occurrence.canReopen && (
      <IconButton
        color="primary"
        onClick={() => onReopen(occurrence.id)}
        disabled={isBusy}
        title="Reabrir"
      >
        {isBusy ? <CircularProgress size={18} /> : <ReplayIcon />}
      </IconButton>
    )}
    {occurrence.canManage && (
      <IconButton color="primary" onClick={() => onEdit(occurrence)} disabled={isBusy} title="Editar">
        <EditOutlinedIcon />
      </IconButton>
    )}
    {occurrence.canManage && (
      <IconButton
        color="error"
        onClick={() => onDelete(occurrence)}
        disabled={isBusy}
        title="Eliminar"
      >
        {actionLoadingId === occurrence.taskId ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
      </IconButton>
    )}
  </Stack>
);

const TaskListTable = ({
  loading,
  occurrences,
  canManageTasks,
  actionLoadingId,
  onComplete,
  onReopen,
  onEdit,
  onDelete,
}) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Tarea</TableCell>
          {canManageTasks && <TableCell>Responsable</TableCell>}
          {canManageTasks && <TableCell>Sucursales</TableCell>}
          {canManageTasks && <TableCell>Fecha</TableCell>}
          {canManageTasks && <TableCell>Tipo</TableCell>}
          <TableCell>Estado</TableCell>
          <TableCell align="right">Acciones</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={canManageTasks ? 7 : 3} align="center" sx={{ py: 5 }}>
              <CircularProgress />
            </TableCell>
          </TableRow>
        ) : (
          occurrences.map((occurrence) => {
            const isBusy = actionLoadingId === occurrence.id || actionLoadingId === occurrence.taskId;

            return (
              <TableRow key={occurrence.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">{occurrence.title}</Typography>
                  {occurrence.description && (
                    <Typography variant="body2" color="text.secondary">
                      {occurrence.description}
                    </Typography>
                  )}
                </TableCell>
                {canManageTasks && (
                  <TableCell>{`${occurrence.assigneeFirstName} ${occurrence.assigneeLastName}`.trim() || occurrence.assigneeEmail}</TableCell>
                )}
                {canManageTasks && (
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {occurrence.locations.map((location) => (
                        <Chip key={`${occurrence.id}-${location.id}`} label={location.name} size="small" />
                      ))}
                    </Stack>
                  </TableCell>
                )}
                {canManageTasks && <TableCell>{formatOccurrenceLabel(occurrence)}</TableCell>}
                {canManageTasks && (
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        size="small"
                        label={occurrence.taskType === 'RECURRING' ? 'Recurrente' : 'Puntual'}
                        color={occurrence.taskType === 'RECURRING' ? 'secondary' : 'default'}
                      />
                      {occurrence.taskType === 'RECURRING' && occurrence.recurrenceType && (
                        <Chip
                          size="small"
                          label={occurrence.recurrenceType === 'WEEKLY' ? 'Semanal' : 'Mensual'}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    size="small"
                    label={occurrence.status === 'COMPLETED' ? 'Completada' : 'Pendiente'}
                    color={occurrence.status === 'COMPLETED' ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell align="right">
                  <TaskOccurrenceActions
                    occurrence={occurrence}
                    isBusy={isBusy}
                    actionLoadingId={actionLoadingId}
                    onComplete={onComplete}
                    onReopen={onReopen}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

const TaskBoardView = ({
  loading,
  columns,
  canManageTasks,
  actionLoadingId,
  onComplete,
  onReopen,
  onEdit,
  onDelete,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
      gap: 2,
      alignItems: 'start',
    }}
  >
    {columns.map((column) => {
      const isToday = column.date.isSame(dayjs(), 'day');

      return (
        <Paper
          key={column.date.format('YYYY-MM-DD')}
          variant="outlined"
          sx={{
            minHeight: 420,
            p: 1.5,
            backgroundColor: isToday ? 'rgba(25, 118, 210, 0.06)' : 'background.paper',
            borderColor: isToday ? 'primary.main' : 'divider',
          }}
        >
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {formatBoardColumnTitle(column.date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {column.occurrences.length} tarea{column.occurrences.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Divider />
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : column.occurrences.length ? (
              <Stack spacing={1.5}>
                {column.occurrences.map((occurrence) => {
                  const isBusy = actionLoadingId === occurrence.id || actionLoadingId === occurrence.taskId;

                  return (
                    <Card key={occurrence.id} variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Stack spacing={1.25}>
                          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                            <Typography variant="subtitle2">{occurrence.title}</Typography>
                            <Chip
                              size="small"
                              label={occurrence.status === 'COMPLETED' ? 'Completada' : 'Pendiente'}
                              color={occurrence.status === 'COMPLETED' ? 'success' : 'warning'}
                            />
                          </Stack>
                          {occurrence.description && (
                            <Typography variant="body2" color="text.secondary">
                              {occurrence.description}
                            </Typography>
                          )}
                          {canManageTasks && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                label={occurrence.taskType === 'RECURRING' ? 'Recurrente' : 'Puntual'}
                                color={occurrence.taskType === 'RECURRING' ? 'secondary' : 'default'}
                              />
                              {occurrence.taskType === 'RECURRING' && occurrence.recurrenceType && (
                                <Chip
                                  size="small"
                                  label={occurrence.recurrenceType === 'WEEKLY' ? 'Semanal' : 'Mensual'}
                                  variant="outlined"
                                />
                              )}
                              <Chip size="small" variant="outlined" label={occurrence.dueTime ? occurrence.dueTime.slice(0, 5) : 'Sin hora'} />
                            </Stack>
                          )}
                          {canManageTasks && (
                            <Typography variant="body2" color="text.secondary">
                              {`${occurrence.assigneeFirstName} ${occurrence.assigneeLastName}`.trim() || occurrence.assigneeEmail}
                            </Typography>
                          )}
                          {canManageTasks && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {occurrence.locations.map((location) => (
                                <Chip key={`${occurrence.id}-${location.id}`} label={location.name} size="small" />
                              ))}
                            </Stack>
                          )}
                          <Divider />
                          <TaskOccurrenceActions
                            occurrence={occurrence}
                            isBusy={isBusy}
                            actionLoadingId={actionLoadingId}
                            onComplete={onComplete}
                            onReopen={onReopen}
                            onEdit={onEdit}
                            onDelete={onDelete}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <Alert severity="info">Sin tareas para este día</Alert>
            )}
          </Stack>
        </Paper>
      );
    })}
  </Box>
);

const TasksPage = () => {
  const { t } = useTranslation();
  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const [canManageTasks, setCanManageTasks] = useState(userPermissions.includes(getPermissionId('MANAGE_TASKS')));

  const [occurrences, setOccurrences] = useState([]);
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selectedAssigneeEmail, setSelectedAssigneeEmail] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState(TASK_VIEW_MODE.LIST);
  const [boardCenterDate, setBoardCenterDate] = useState(dayjs());
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOccurrence, setEditingOccurrence] = useState(null);
  const [deletingOccurrence, setDeletingOccurrence] = useState(null);
  const [completingOccurrence, setCompletingOccurrence] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const accessibleLocationIds = useMemo(() => new Set(locations.map((location) => location.id)), [locations]);

  const assignableStaff = useMemo(() => {
    if (!canManageTasks) return [];
    return staff.filter((staffMember) => (staffMember.locationIds || []).some((locationId) => accessibleLocationIds.has(locationId)));
  }, [accessibleLocationIds, canManageTasks, staff]);

  const selectedAssignee = useMemo(
    () => assignableStaff.find((staffMember) => staffMember.email === selectedAssigneeEmail) || null,
    [assignableStaff, selectedAssigneeEmail]
  );

  const boardDates = useMemo(
    () => BOARD_DAY_OFFSETS.map((offset) => boardCenterDate.add(offset, 'day')),
    [boardCenterDate]
  );

  const effectiveFromDate = useMemo(
    () => (viewMode === TASK_VIEW_MODE.BOARD ? boardDates[0] : fromDate),
    [boardDates, fromDate, viewMode]
  );

  const effectiveToDate = useMemo(
    () => (viewMode === TASK_VIEW_MODE.BOARD ? boardDates[boardDates.length - 1] : toDate),
    [boardDates, toDate, viewMode]
  );

  const loadReferenceData = async () => {
    if (!canManageTasks) return;

    const [staffData, locationData] = await Promise.all([getStaff(), getLocations()]);
    setStaff(staffData || []);
    setLocations(locationData || []);
  };

  const loadOccurrences = async () => {
    try {
      setLoading(true);
      const data = await StaffTaskService.getOccurrences({
        from: effectiveFromDate.format('YYYY-MM-DD'),
        to: effectiveToDate.format('YYYY-MM-DD'),
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        assigneeEmail: canManageTasks ? selectedAssigneeEmail : undefined,
        locationId: canManageTasks ? selectedLocationId : undefined,
      });
      setOccurrences(data.occurrences || []);
      setCanManageTasks((previous) => previous || data.canManage === true);
    } catch (error) {
      console.error('Error loading tasks:', error);
      SnackbarUtils.error('No pudimos cargar las tareas');
      setOccurrences([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const profile = await getProfile();
        if (profile?.role?.name === 'SUPERADMIN') {
          setCanManageTasks(true);
        }
        if (canManageTasks) {
          await loadReferenceData();
        }
      } catch (error) {
        console.error('Error loading task reference data:', error);
        SnackbarUtils.error('No pudimos cargar staff y sucursales');
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    if (!canManageTasks) return;
    loadReferenceData();
  }, [canManageTasks]);

  useEffect(() => {
    loadOccurrences();
  }, [canManageTasks, selectedAssigneeEmail, selectedLocationId, statusFilter, effectiveFromDate, effectiveToDate]);

  const boardColumns = useMemo(() => {
    const occurrencesByDate = occurrences.reduce((accumulator, occurrence) => {
      const key = dayjs(occurrence.occurrenceDate).format('YYYY-MM-DD');
      if (!accumulator[key]) {
        accumulator[key] = [];
      }
      accumulator[key].push(occurrence);
      return accumulator;
    }, {});

    return boardDates.map((date) => {
      const key = date.format('YYYY-MM-DD');
      const dayOccurrences = [...(occurrencesByDate[key] || [])].sort((left, right) => {
        const leftTime = left.dueTime || '99:99:99';
        const rightTime = right.dueTime || '99:99:99';

        if (leftTime !== rightTime) {
          return leftTime.localeCompare(rightTime);
        }

        return left.title.localeCompare(right.title);
      });
      return {
        date,
        occurrences: dayOccurrences,
      };
    });
  }, [boardDates, occurrences]);

  const handleOpenCreate = () => {
    setEditingOccurrence(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (occurrence) => {
    setEditingOccurrence(occurrence);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingOccurrence(null);
  };

  const handleCloseDeleteDialog = () => {
    if (actionLoadingId) return;
    setDeletingOccurrence(null);
  };

  const handleCloseCompleteDialog = () => {
    if (actionLoadingId) return;
    setCompletingOccurrence(null);
  };

  const handleSaveTask = async (payload) => {
    try {
      setSaving(true);
      if (editingOccurrence) {
        await StaffTaskService.updateTask(editingOccurrence.taskId, payload);
        SnackbarUtils.success('Tarea actualizada');
      } else {
        await StaffTaskService.createTask(payload);
        SnackbarUtils.success('Tarea creada');
      }
      setModalOpen(false);
      setEditingOccurrence(null);
      await loadOccurrences();
    } catch (error) {
      console.error('Error saving task:', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos guardar la tarea');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (occurrence) => {
    try {
      setActionLoadingId(occurrence.taskId);
      await StaffTaskService.deleteTask(occurrence.taskId);
      SnackbarUtils.success('Se cancelaron todas las pendientes de la tarea');
      setDeletingOccurrence(null);
      await loadOccurrences();
    } catch (error) {
      console.error('Error deleting task:', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos eliminar la tarea');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteOccurrence = async (occurrence) => {
    try {
      setActionLoadingId(occurrence.id);
      await StaffTaskService.deleteOccurrence(occurrence.id);
      SnackbarUtils.success(occurrence.taskType === 'RECURRING' ? 'Se eliminó solo esta ocurrencia' : 'Tarea eliminada');
      setDeletingOccurrence(null);
      await loadOccurrences();
    } catch (error) {
      console.error('Error deleting occurrence:', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos eliminar la tarea');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRequestCompleteOccurrence = (occurrence) => {
    setCompletingOccurrence(occurrence);
  };

  const handleCompleteOccurrence = async (occurrence) => {
    try {
      setActionLoadingId(occurrence.id);
      await StaffTaskService.completeOccurrence(occurrence.id);
      SnackbarUtils.success('Tarea completada');
      setCompletingOccurrence(null);
      await loadOccurrences();
    } catch (error) {
      console.error('Error completing task:', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos completar la tarea');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReopenOccurrence = async (occurrenceId) => {
    try {
      setActionLoadingId(occurrenceId);
      await StaffTaskService.reopenOccurrence(occurrenceId);
      SnackbarUtils.success('Tarea reabierta');
      await loadOccurrences();
    } catch (error) {
      console.error('Error reopening task:', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos reabrir la tarea');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('PENDING');
    setSelectedAssigneeEmail('');
    setSelectedLocationId('');
    setFromDate(dayjs());
    setToDate(dayjs());
    setBoardCenterDate(dayjs());
  };

  return (
    <Box p={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <PageHeader
          title={t('tasks') || 'Tareas'}
          subtitle={canManageTasks ? 'Administra tareas del staff y sus recurrencias' : 'Mira y completa tus tareas asignadas'}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 0.5, borderRadius: 999 }}>
            <IconButton
              color={viewMode === TASK_VIEW_MODE.LIST ? 'primary' : 'default'}
              onClick={() => setViewMode(TASK_VIEW_MODE.LIST)}
              title="Vista lista"
            >
              <ViewListOutlinedIcon />
            </IconButton>
            <IconButton
              color={viewMode === TASK_VIEW_MODE.BOARD ? 'primary' : 'default'}
              onClick={() => setViewMode(TASK_VIEW_MODE.BOARD)}
              title="Vista tablero"
            >
              <DashboardOutlinedIcon />
            </IconButton>
          </Paper>
          {canManageTasks && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Nueva tarea
            </Button>
          )}
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select label="Estado" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {viewMode === TASK_VIEW_MODE.LIST ? (
            <>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Desde"
                  format="DD/MM/YYYY"
                  value={fromDate}
                  onChange={(value) => value && setFromDate(value)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Hasta"
                  format="DD/MM/YYYY"
                  value={toDate}
                  onChange={(value) => value && setToDate(value)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Fecha central"
                format="DD/MM/YYYY"
                value={boardCenterDate}
                onChange={(value) => value && setBoardCenterDate(value)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          )}

          {canManageTasks && (
            <>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  value={selectedAssignee}
                  options={assignableStaff}
                  onChange={(_, value) => setSelectedAssigneeEmail(value?.email || '')}
                  getOptionLabel={formatPersonName}
                  isOptionEqualToValue={(option, value) => option.email === value.email}
                  renderInput={(params) => <TextField {...params} label="Responsable" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  value={locations.find((location) => location.id === selectedLocationId) || null}
                  options={locations}
                  onChange={(_, value) => setSelectedLocationId(value?.id || '')}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} label="Sucursal" fullWidth />}
                />
              </Grid>
            </>
          )}

          <Grid
            item
            xs={12}
            md={canManageTasks ? (viewMode === TASK_VIEW_MODE.LIST ? 2 : 4) : (viewMode === TASK_VIEW_MODE.LIST ? 6 : 8)}
            sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' }, alignItems: 'stretch' }}
          >
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              sx={{ minHeight: 56, width: { xs: '100%', md: 'auto' } }}
            >
              Limpiar filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {!loading && !occurrences.length && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No encontramos tareas para ese rango.
        </Alert>
      )}

      {viewMode === TASK_VIEW_MODE.LIST ? (
        <TaskListTable
          loading={loading}
          occurrences={occurrences}
          canManageTasks={canManageTasks}
          actionLoadingId={actionLoadingId}
          onComplete={handleRequestCompleteOccurrence}
          onReopen={handleReopenOccurrence}
          onEdit={handleOpenEdit}
          onDelete={setDeletingOccurrence}
        />
      ) : (
        <TaskBoardView
          loading={loading}
          columns={boardColumns}
          canManageTasks={canManageTasks}
          actionLoadingId={actionLoadingId}
          onComplete={handleRequestCompleteOccurrence}
          onReopen={handleReopenOccurrence}
          onEdit={handleOpenEdit}
          onDelete={setDeletingOccurrence}
        />
      )}

      {canManageTasks && (
        <TaskFormDialog
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveTask}
          saving={saving}
          staffOptions={assignableStaff}
          locationOptions={locations}
          initialValue={editingOccurrence}
        />
      )}
      {canManageTasks && (
        <TaskDeleteDialog
          open={Boolean(deletingOccurrence)}
          onClose={handleCloseDeleteDialog}
          onDeleteOccurrence={() => deletingOccurrence && handleDeleteOccurrence(deletingOccurrence)}
          onDeleteTask={() => deletingOccurrence && handleDeleteTask(deletingOccurrence)}
          deleting={Boolean(actionLoadingId)}
          occurrence={deletingOccurrence}
        />
      )}
      <TaskCompleteDialog
        open={Boolean(completingOccurrence)}
        onClose={handleCloseCompleteDialog}
        onConfirm={() => completingOccurrence && handleCompleteOccurrence(completingOccurrence)}
        completing={Boolean(completingOccurrence) && actionLoadingId === completingOccurrence?.id}
        occurrence={completingOccurrence}
      />
    </Box>
  );
};

export default TasksPage;
